import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  Grid,
  Typography,
} from "@mui/material";
import { grey } from '@mui/material/colors';
import { formatMoney } from "../../utils/utils";

const CELL_WIDTH = 56;
const ESFERA_COL_WIDTH = 88;
const MAX_DIGITS = 5;

// Celda editable: memoizada para que solo re-renderice cuando cambia value/isDisabled
const EditableCell = React.memo(function EditableCell({
  rowIndex,
  colIndex,
  value,
  onBlur,
  onKeyDown,
  registerRef,
  isDisabled,
  action,
}) {
  const [inputValue, setInputValue] = useState(value);
  const disabled = isDisabled || action === "view" || action === "delete";

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleRef = useCallback(
    (el) => {
      registerRef(rowIndex, colIndex, el);
    },
    [rowIndex, colIndex, registerRef]
  );

  return (
    <TableCell
      align="center"
      sx={{
        padding: 0,
        width: CELL_WIDTH,
        minWidth: CELL_WIDTH,
        maxWidth: CELL_WIDTH,
        height: 36,
        boxSizing: "border-box",
        verticalAlign: "middle",
      }}
    >
      <input
        ref={handleRef}
        type="text"
        inputMode="numeric"
        value={inputValue}
        onChange={(e) => {
          const v = e.target.value.replace(/\D/g, "").slice(0, MAX_DIGITS);
          setInputValue(v);
        }}
        onBlur={() => onBlur(rowIndex, colIndex, inputValue)}
        onKeyDown={(e) => onKeyDown(e, rowIndex, colIndex)}
        maxLength={MAX_DIGITS}
        disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        style={{
          width: "100%",
          maxWidth: CELL_WIDTH - 8,
          height: 30,
          margin: 0,
          padding: "2px 4px",
          border: "1px solid #ccc",
          borderRadius: 4,
          fontSize: 12,
          textAlign: "center",
          backgroundColor: disabled ? "#e0e0e0" : "#fff",
          cursor: disabled ? "not-allowed" : "text",
          boxSizing: "border-box",
        }}
      />
    </TableCell>
  );
});
//
// ##########################################################################
//
const IngresoCristalDinamicTable = ({
  action,
  esfMax,
  esfMin,
  cilMax,
  cilMin,
  rangosList,
  correccionesSinPrecioList,
  setIngresoCristalList,
  ingresoCristalList,
}) => {
  const interv = 0.25;

  const rowValues = useMemo(() => {
    const count = Math.round((esfMax - esfMin) / interv) + 1;
    if (count <= 0) return [];
    return Array.from({ length: count }, (_, i) => esfMin + i * interv);
  }, [esfMin, esfMax]);

  const columnValues = useMemo(() => {
    const count = Math.round((cilMax - cilMin) / interv) + 1;
    if (count <= 0) return [];
    return Array.from({ length: count }, (_, i) => cilMin + i * interv);
  }, [cilMin, cilMax]);

  const numRows = rowValues.length;
  const numCols = columnValues.length;

  const [data, setData] = useState(() =>
    numRows && numCols ? rowValues.map(() => columnValues.map(() => "")) : []
  );

  const [totales, setTotales] = useState({
    cantidadArticulos: 0,
    cantCristales: 0,
    subtotal: 0,
    iva: 0,
    total: 0,
  });

  const initializedRef = useRef(false);
  const inputRefs = useRef({});
  const dataRef = useRef(data);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const tableMinWidth = ESFERA_COL_WIDTH + numCols * CELL_WIDTH;

  useEffect(() => {
    if (numRows === 0 || numCols === 0) {
      setData([]);
      return;
    }
    setData((prev) => {
      if (prev.length === numRows && prev[0]?.length === numCols) return prev;
      return rowValues.map(() => columnValues.map(() => ""));
    });
  }, [numRows, numCols, rowValues, columnValues]);
  //
  // ##########################################################################
  //
  const getValoresRango = useCallback(
    (par_esfera, par_cilindro) => {

      let _costoPesos = 0;
      let _porcentajeDescuento = 0;
      let _costoReal = 0;

      for (let i = 0; i < rangosList.length; i++) {
        const _rango = rangosList[i];
        if (
          par_esfera >= _rango.esfera_desde &&
          par_esfera <= _rango.esfera_hasta &&
          par_cilindro >= _rango.cilindro_desde &&
          par_cilindro <= _rango.cilindro_hasta
        ) {
          _costoPesos = _rango.costo_pesos;
          _porcentajeDescuento = _rango.porcentaje_descuento;
          _costoReal = _rango.costo_real;
          break;
        }
      }
      return [_costoPesos, _porcentajeDescuento, _costoReal];
    },
    [rangosList]
  );
  //
  // ##########################################################################
  //
  const calculate = useCallback(
    (newData) => {

      let _cantidadArticulos = 0;
      let _cantidadCristales = 0;
      let _subtotal = 0;

      const newIngresoArticuloList = [];

      newData.forEach((row, rowIndex) => {
        row.forEach((cantidadStr, colIndex) => {

          const _cantidadEnCelda = parseInt(cantidadStr, 10) || 0;

          if (_cantidadEnCelda > 0) {

            const [costoPesos, porcentajeDescuento, costoReal] = getValoresRango(
              rowValues[rowIndex],
              columnValues[colIndex]
            );

            _cantidadArticulos += 1;
            _cantidadCristales += _cantidadEnCelda;
            _subtotal += costoReal * _cantidadEnCelda;

            newIngresoArticuloList.push({
              esfera: rowValues[rowIndex],
              cilindro: columnValues[colIndex],
              cantidad: _cantidadEnCelda,
              costo_pesos: costoPesos,
              porcentaje_descuento: porcentajeDescuento,
              costo_real: costoReal,
              tasa_iva: 21,
              tipo_articulo: "cristal",
            });
          }
        });
      });

      const _iva = _subtotal * 0.21;
      const _total = _subtotal + _iva;

      setTotales({
        ...totales,
        cantidadArticulos: _cantidadArticulos,
        cantCristales: _cantidadCristales,
        subtotal: _subtotal,
        iva: _iva,
        total: _total,
      });

      setIngresoCristalList(newIngresoArticuloList);
    },
    [rowValues, columnValues, getValoresRango, setIngresoCristalList]
  );
  //
  // ###########################################################################
  //
  const disabledCellsSet = useMemo(() => {
    // Set de celdas deshabilitadas (clave "r-c") para O(1) y evitar .some() en cada celda
    const set = new Set();
    if (action === "view" || action === "delete") {
      for (let r = 0; r < numRows; r++) for (let c = 0; c < numCols; c++) set.add(`${r}-${c}`);
    } else {
      correccionesSinPrecioList.forEach((csp) => {
        for (let r = 0; r < rowValues.length; r++) {
          for (let c = 0; c < columnValues.length; c++) {
            if (rowValues[r] === csp.esfera && columnValues[c] === csp.cilindro) set.add(`${r}-${c}`);
          }
        }
      });
    }
    return set;
  }, [action, correccionesSinPrecioList, rowValues, columnValues, numRows, numCols]);
  //
  // ###########################################################################
  //
  const isCellDisabled = useCallback(
    (r, c) => disabledCellsSet.has(`${r}-${c}`),
    [disabledCellsSet]
  );
  //
  // ###########################################################################
  //
  const getNextCell = useCallback(
    (rowIndex, colIndex, shiftKey) => {
      if (shiftKey) {
        let c = colIndex - 1;
        let r = rowIndex;
        if (c < 0) {
          r -= 1;
          c = numCols - 1;
        }
        if (r < 0) return null;
        while (r >= 0 && isCellDisabled(r, c)) {
          c -= 1;
          if (c < 0) {
            r -= 1;
            c = numCols - 1;
          }
        }
        return r >= 0 ? { r, c } : null;
      } else {
        let c = colIndex + 1;
        let r = rowIndex;
        if (c >= numCols) {
          r += 1;
          c = 0;
        }
        if (r >= numRows) return null;
        while (r < numRows && isCellDisabled(r, c)) {
          c += 1;
          if (c >= numCols) {
            r += 1;
            c = 0;
          }
        }
        return r < numRows ? { r, c } : null;
      }
    },
    [numRows, numCols, isCellDisabled]
  );
  //
  // ###########################################################################
  //
  const registerRef = useCallback((rowIndex, colIndex, el) => {
    const key = `${rowIndex}-${colIndex}`;
    if (el) {
      inputRefs.current[key] = el;
    } else {
      delete inputRefs.current[key];
    }
  }, []);
  //
  // ###########################################################################
  //
  const handleKeyDown = useCallback(
    (e, rowIndex, colIndex) => {
      if (e.key !== "Tab") {
        return
      };
      const next = getNextCell(rowIndex, colIndex, e.shiftKey);
      if (next) {
        e.preventDefault();
        e.stopPropagation();
        const key = `${next.r}-${next.c}`;
        requestAnimationFrame(() => {
          const el = inputRefs.current[key];
          if (el && el.focus) {
            el.focus();
            el.select && el.select();
          }
        });
      }
    },
    [getNextCell]
  );
  //
  // ###########################################################################
  //
  const handleBlur = useCallback(
    (rowIndex, colIndex, value) => {
      const val = String(value ?? "").replace(/\D/g, "").slice(0, MAX_DIGITS);
      if (!new RegExp(`^\\d{0,${MAX_DIGITS}}$`).test(val)) {
        return;
      };
      const prev = dataRef.current;
      const newData = prev.map((row, r) =>
        r === rowIndex
          ? row.map((cell, c) => (c === colIndex ? val : cell))
          : row
      );
      setData(newData);
      // Recálculo en el siguiente tick para no actualizar el padre durante el render del hijo
      setTimeout(() => calculate(newData), 0);
    },
    [calculate]
  );
  //
  // ###########################################################################
  //
  useEffect(() => {
    if (initializedRef.current) return;
    if (!ingresoCristalList?.length || numRows === 0 || numCols === 0) return;

    const newData = rowValues.map(() => columnValues.map(() => ""));
    ingresoCristalList.forEach((item) => {
      const ri = rowValues.findIndex((v) => v === item.esfera);
      const ci = columnValues.findIndex((v) => v === item.cilindro);
      if (ri !== -1 && ci !== -1) newData[ri][ci] = String(item.cantidad);
    });
    setData(newData);
    calculate(newData);
    initializedRef.current = true;
  }, [ingresoCristalList, rowValues, columnValues, calculate, numRows, numCols]);

  const readOnly = action === "view" || action === "delete";
  const hasData = numRows > 0 && numCols > 0;

  if (!hasData) {
    return (
      <Paper sx={{ margin: "10px", padding: "10px", border: "1px solid #e0e0e0" }}>
        <Box sx={{ height: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography color="text.secondary">
            No hay datos para mostrar. Verifique los criterios de búsqueda.
          </Typography>
        </Box>
      </Paper>
    );
  }
  //
  // ###########################################################################
  //
  return (
    <Paper
      sx={{
        margin: "10px",
        padding: "10px",
        width: "100%",
        boxSizing: "border-box",
        border: "1px solid #e0e0e0",
        overflow: "hidden",
      }}
    >
      <TableContainer
        component={Paper}
        sx={{
          width: "100%",
          maxHeight: 400,
          overflowX: "auto",
          overflowY: "auto",
        }}
      >
        <Table
          stickyHeader
          size="small"
          sx={{
            tableLayout: "fixed",
            minWidth: tableMinWidth,
            width: "100%",
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                align="center"
                sx={{
                  width: ESFERA_COL_WIDTH,
                  minWidth: ESFERA_COL_WIDTH,
                  position: "sticky",
                  left: 0,
                  zIndex: 2,
                  backgroundColor: "background.paper",
                  borderRight: "1px solid",
                  borderColor: "divider",
                }}
              >
                Esfera / Cilindro
              </TableCell>
              {columnValues.map((col, i) => (
                <TableCell
                  key={i}
                  align="center"
                  sx={{
                    width: CELL_WIDTH,
                    minWidth: CELL_WIDTH,
                    boxSizing: "border-box",
                  }}
                >
                  {col.toFixed(2)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rowValues.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                <TableCell
                  align="center"
                  sx={{
                    width: ESFERA_COL_WIDTH,
                    minWidth: ESFERA_COL_WIDTH,
                    position: "sticky",
                    left: 0,
                    zIndex: 1,
                    backgroundColor: "background.paper",
                    borderRight: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  {row.toFixed(2)}
                </TableCell>
                {columnValues.map((_, colIndex) => (
                  <EditableCell
                    key={colIndex}
                    rowIndex={rowIndex}
                    colIndex={colIndex}
                    value={data[rowIndex]?.[colIndex] ?? ""}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    registerRef={registerRef}
                    isDisabled={disabledCellsSet.has(`${rowIndex}-${colIndex}`)}
                    action={action}
                  />
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ my: 3 }}>
        <Grid container spacing={1} sx={{
          display: "flex",
          justifyContent: "flex-end",
          mb: 2,
        }}>

          <Grid item xs={12} sm={12} md={1} lg={1}>
            <TextField
              sx={{
                width: "100%",
                "& .MuiOutlinedInput-root": {
                  backgroundColor: grey[300],
                },
              }}
              fullWidth
              label="Cant Cristales"
              variant="outlined"
              size="small"
              inputProps={{
                readOnly: true,
                style: { textAlign: "right" },
              }}
              value={totales.cantCristales}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={1} lg={1}>
            <TextField
              sx={{
                width: "100%",
                "& .MuiOutlinedInput-root": {
                  backgroundColor: grey[300],
                },
              }}
              fullWidth
              label="Cant Artículos"
              variant="outlined"
              size="small"
              inputProps={{
                readOnly: true,
                style: { textAlign: "right" },
              }}
              value={totales.cantidadArticulos}
            />
          </Grid>

          <Grid item xs={12} md={6} lg={2}>
            <TextField
              sx={{
                width: "100%",
                "& .MuiOutlinedInput-root": {
                  backgroundColor: grey[300],
                },
              }}
              fullWidth
              label="Subtotal"
              variant="outlined"
              size="small"
              inputProps={{
                readOnly: true,
                style: { textAlign: "right" },
              }}
              value={formatMoney(totales.subtotal)}
            />

          </Grid>
          <Grid item xs={12} md={6} lg={2}>
            <TextField
              sx={{
                width: "100%",
                "& .MuiOutlinedInput-root": {
                  backgroundColor: grey[300],
                },
              }}
              fullWidth
              label="IVA"
              variant="outlined"
              size="small"
              inputProps={{
                readOnly: true,
                style: { textAlign: "right" },
              }}
              value={formatMoney(totales.iva)}
            />

          </Grid>
          <Grid item xs={12} md={6} lg={2}>
            <TextField
              sx={{
                width: "100%",
                "& .MuiOutlinedInput-root": {
                  backgroundColor: grey[300],
                },
              }}
              fullWidth
              label="Total"
              variant="outlined"
              size="small"
              inputProps={{
                readOnly: true,
                style: { textAlign: "right" },
              }}
              value={formatMoney(totales.total)}
            />
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};
export default IngresoCristalDinamicTable;
